<script setup lang="ts">
    import ActionDeleteIcon from "@/assets/actionIcons/delete.svg";
    import ActionEditIcon from "@/assets/actionIcons/edit.svg";
    import ActionShowIcon from "@/assets/actionIcons/show.svg";
    import ActionErrorIcon from "@/assets/info_grey.svg";
    import { CellContentAlignment, CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction } from "@/components/KTable/types";
    import { useStore } from "@/store";
    import { StoredReceptforskrivning } from "@/store/modules/recept/receptforskrivningModule";
    import CellLakemedel from "@/tabs/Receptforskrivning/cells/CellLakemedel.vue";
    import CellStatus from "@/tabs/Receptforskrivning/cells/CellStatus.vue";
    import ReceptforskrivningSamlingPortal from "@/tabs/Receptforskrivning/Forskrivning/Samling/ReceptforskrivningSamlingPortal.vue";
    import { sendForskrivningar } from "@/tabs/Receptforskrivning/helpers";
    import { LakemedelDisplay } from "@/tabs/Receptforskrivning/types";
    import { flashMessage, flashWarning, openDialog } from "@/utils";
    import emitter from "tiny-emitter/instance";
    import { computed, defineProps, ref, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        receptforskrivningId?: string;
    }>();

    const emit = defineEmits<{
        (event: "cancel", payload?: { hasUnsavedData?: boolean }): void;
        (e: "action", payload: { step: "form" | "display" | "list"; id?: string; editingSamling?: boolean }): void;
    }>();

    const { tabId, receptforskrivningId } = toRefs(props);

    const loading = ref(false);

    const receptforskrivningar = computed(() => {
        const forskrivningar = store.getters["receptforskrivning/receptforskrivningar"] as StoredReceptforskrivning[];

        return forskrivningar.map((receptforskrivning) => {
            const lakemedel = store.getters["lakemedel/lakemedelById"]({
                id: receptforskrivning.lakemedelId,
                displayMode: true,
            }) as LakemedelDisplay;

            return {
                id: receptforskrivning.id,
                lakemedel: lakemedel.lakemedel,
                styrka: lakemedel.styrka,
                substans: lakemedel.substans,
                atc: lakemedel.atc,
                form: lakemedel.form,
                doseringsanvisning: receptforskrivning.forhandsvisning,
                status: {
                    forskrivningId: receptforskrivning.id,
                },
            };
        });
    });

    const columns: Column[] = [
        {
            field: "lakemedel",
            header: "Läkemedel",
            minWidth: CellContentWidth.m,
            bodyTemplate: CellLakemedel,
        },
        { field: "styrka", header: "Styrka", minWidth: CellContentWidth.xs },
        { field: "substans", header: "Substans", minWidth: CellContentWidth.xs },
        { field: "atc", header: "ATC", minWidth: CellContentWidth.xxs, limitWidth: true },
        { field: "form", header: "Form" },
        {
            field: "doseringsanvisning",
            header: "Doseringsanvisning",
            truncate: true,
            lines: 2,
            minWidth: CellContentWidth.xl,
        },
        {
            field: "status",
            header: "Status",
            bodyTemplate: CellStatus,
            limitWidth: true,
            frozen: true,
            alignFrozen: "right",
            alignContent: CellContentAlignment.Center,
        },
    ];

    const onAction = ({ step, id }: { step: "form" | "display" | "list"; id?: string }) => {
        emit("action", { step, id, editingSamling: true });
    };

    const actions: KTableAction[] = [
        {
            title: "Visa felmeddelande",
            icon: ActionErrorIcon,
            show: (data) => {
                return (
                    store.getters["receptforskrivning/statusById"]({ id: (data as StoredReceptforskrivning).id }) ===
                    "Error"
                );
            },
            callback: ({ data }) => {
                const forskrivning: StoredReceptforskrivning = store.getters[
                    "receptforskrivning/receptforskrivningById"
                ]({ id: data.id });
                const errorMessages = forskrivning.errorMessages
                    ?.filter((message) => message.code.match(/^[a-zA-Z]\./))
                    .map((message) => message.display);

                openDialog({
                    description: errorMessages?.length
                        ? "Förskrivningen innehåller fel"
                        : "Kunde inte skicka förskrivningen",
                    lines: errorMessages?.length ? errorMessages : ["Okänt fel"],
                    type: "error",
                });
            },
        },
        {
            title: "Förhandsgranska förskrivning",
            icon: ActionShowIcon,
            show: (data) =>
                store.getters["receptforskrivning/statusById"]({ id: (data as StoredReceptforskrivning).id }) !==
                "Success",
            callback: ({ data }: { data: unknown }) => {
                onAction({ step: "display", id: (data as StoredReceptforskrivning).id });
            },
        },
        {
            title: "Redigera förskrivning",
            icon: ActionEditIcon,
            show: (data) =>
                store.getters["receptforskrivning/statusById"]({ id: (data as StoredReceptforskrivning).id }) !==
                "Success",
            callback: ({ data }: { data: unknown }) => {
                onAction({ step: "form", id: (data as StoredReceptforskrivning).id });
            },
        },
        {
            title: "Ta bort",
            icon: ActionDeleteIcon,
            show: (data) =>
                store.getters["receptforskrivning/statusById"]({ id: (data as StoredReceptforskrivning).id }) !==
                "Success",
            callback: ({ data }: { data: unknown }) => {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Är du säker på att du vill ta bort förskrivningen?",
                    buttons: [
                        {
                            title: "Nej",
                            type: "secondary",
                        },
                        {
                            title: "Ja, ta bort förskrivningen",
                            type: "primary",
                            action: () => {
                                store.commit("receptforskrivning/removeReceptforskrivning", {
                                    id: (data as StoredReceptforskrivning).id,
                                });
                            },
                        },
                    ],
                });
            },
        },
    ];

    const onSubmit = async () => {
        loading.value = true;

        const promiseResponses = await sendForskrivningar({
            receptforskrivningar: store.getters["receptforskrivning/receptforskrivningar"].filter(
                (forskrivning: StoredReceptforskrivning) =>
                    store.getters["receptforskrivning/statusById"]({ id: forskrivning.id }) !== "Success"
            ),
            headers: store.getters["nll/headers"],
            saveErrors: true,
        });

        loading.value = false;

        if (promiseResponses?.every((response) => response.status === "fulfilled")) {
            flashMessage("Förskrivningar skickade");
            store.dispatch("tabs/closeTab", tabId?.value);
        } else {
            flashWarning("Några av förskrivningar kunde inte skickas");
        }
    };

    const onCancel = () => {
        emit("cancel");
    };
</script>

<template>
    <div class="receptforskrivning-samling">
        <KTable
            name="receptforskrivning-samling"
            :value="receptforskrivningar"
            :columns="columns"
            :actions="actions"
            :loading="loading"
            loadingMessage="Skickar receptförskrivningar..."
        >
            <template #header>
                <h3>{{ `Samling förskrivningar (${receptforskrivningar?.length ?? 0})` }}</h3>
            </template>
        </KTable>
        <ReceptforskrivningSamlingPortal
            :tab-id="tabId"
            @accept="onSubmit"
            @cancel="onCancel"
            @action="onAction"
            :loading="loading"
        />
    </div>
</template>

<style lang="scss" scoped>
    .receptforskrivning-samling {
    }
</style>
